import React from "react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatRupiah } from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import { Check, X, Search, ChevronLeft, ChevronRight } from "lucide-react";

const PER_PAGE = 10;

export default function AdminWithdrawals() {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);

  const load = async () => {
    try {
      const r = await api.get("/admin/withdrawals");
      setItems(r.data);
    } catch {}
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 7000);
    return () => clearInterval(t);
  }, []);

  const filtered = items.filter((d) => {
    const q = search.toLowerCase();
    return d.user_name?.toLowerCase().includes(q) || d.user_phone?.includes(q) || d.bank_name?.toLowerCase().includes(q) || d.account_no?.includes(q);
  });

  const totalPages = Math.ceil(filtered.length / PER_PAGE);
  const pageData = filtered.slice(page * PER_PAGE, (page + 1) * PER_PAGE);

  const action = async (id, status) => {
    try {
      const note = status === "rejected" ? (window.prompt("Alasan penolakan (opsional):", "") || "") : "";
      await api.put(`/admin/withdrawals/${id}`, { status, note });
      toast.success(`Penarikan ${status}`);
      load();
    } catch { toast.error("Gagal update status"); }
  };

  return (
    <div data-testid="admin-withdrawals">
      <div className="label-mono text-zinc-500">// CASH OUT</div>
      <h1 className="font-display text-3xl md:text-4xl font-black uppercase mt-1">Penarikan Dana</h1>
      <div className="mt-6 border border-black bg-white overflow-x-auto">
        <div className="p-3 border-b border-black flex flex-wrap justify-between items-center gap-2">
          <div className="flex items-center gap-3">
            <div className="label-mono text-zinc-500">{filtered.length} transaksi</div>
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
              <input value={search} onChange={(e) => { setSearch(e.target.value); setPage(0); }} placeholder="Cari nama / HP / bank..." className="input-brutal pl-8 py-1.5 text-xs w-52" />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="font-mono text-[10px] text-zinc-500">{page * PER_PAGE + 1}–{Math.min((page + 1) * PER_PAGE, filtered.length)} dari {filtered.length}</span>
            <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}
              className="p-1.5 border border-black hover:bg-black hover:text-white disabled:opacity-30">
              <ChevronLeft className="w-3 h-3" />
            </button>
            <span className="font-mono text-[10px]">{page + 1}/{totalPages || 1}</span>
            <button onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1}
              className="p-1.5 border border-black hover:bg-black hover:text-white disabled:opacity-30">
              <ChevronRight className="w-3 h-3" />
            </button>
          </div>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 border-b border-black">
            <tr>
              <th className="p-3 text-left label-mono">USER</th>
              <th className="p-3 text-right label-mono">JUMLAH</th>
              <th className="p-3 text-left label-mono hidden md:table-cell">BANK</th>
              <th className="p-3 text-left label-mono">STATUS</th>
              <th className="p-3 text-left label-mono hidden md:table-cell">TANGGAL</th>
              <th className="p-3 text-right label-mono">AKSI</th>
            </tr>
          </thead>
          <tbody>
            {pageData.map((d) => (
              <tr key={d.id} className="border-b border-zinc-200" data-testid={`wd-row-${d.id}`}>
                <td className="p-3">
                  <div className="font-mono text-xs font-bold">{d.user_name}</div>
                  <div className="font-mono text-[10px] text-zinc-500">{d.user_phone}</div>
                </td>
                <td className="p-3 text-right font-mono text-xs font-bold">{formatRupiah(d.amount)}</td>
                <td className="p-3 hidden md:table-cell font-mono text-[10px]">
                  <div>{d.bank_name} · {d.account_no}</div>
                  <div className="text-zinc-500">{d.account_name}</div>
                </td>
                <td className="p-3"><StatusBadge status={d.status} /></td>
                <td className="p-3 hidden md:table-cell font-mono text-[10px]">{new Date(d.created_at).toLocaleString("en-US")}</td>
                <td className="p-3 text-right space-x-1">
                  {d.status === "pending" && (
                    <span className="inline-flex gap-1">
                      <button onClick={() => action(d.id, "approved")} className="p-1.5 border border-black bg-[#00C853] text-white hover:bg-black" data-testid={`wd-approve-${d.id}`}><Check className="w-3 h-3" /></button>
                      <button onClick={() => action(d.id, "rejected")} className="p-1.5 border border-black hover:bg-[#FF3333] hover:text-white" data-testid={`wd-reject-${d.id}`}><X className="w-3 h-3" /></button>
                    </span>
                  )}
                </td>
              </tr>
            ))}
            {pageData.length === 0 && (
              <tr><td colSpan={6} className="p-8 text-center font-mono text-xs text-zinc-500">// {search ? `tidak ada hasil "${search}"` : "no withdrawals"}</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}